import { datadogRum, type RumGlobal } from '@datadog/browser-rum';

import { APP_VERSION, DATADOG_APPLICATION_ID, DATADOG_CLIENT_TOKEN, DATADOG_ENABLED, DATADOG_ENVIRONMENT, DATADOG_SERVICE, DATADOG_TRACING_URL, DATADOG_VERSION, VERCEL_DEPLOYMENT_ID } from 'state_management/env_constants';

export type DatadogRumInitOptions = {
  appVersion: string;
  applicationId: string;
  clientToken: string;
  deploymentId: string;
  enabled: boolean;
  environment: string;
  remoteBaseUrl: string;
  rum: Pick<RumGlobal, 'init' | 'setGlobalContextProperty'> | undefined;
  service: string;
  version: string;
};

const getDefaultOptions = (): DatadogRumInitOptions => ({
  appVersion: APP_VERSION,
  applicationId: DATADOG_APPLICATION_ID,
  clientToken: DATADOG_CLIENT_TOKEN,
  deploymentId: VERCEL_DEPLOYMENT_ID,
  enabled: DATADOG_ENABLED,
  environment: DATADOG_ENVIRONMENT,
  remoteBaseUrl: DATADOG_TRACING_URL,
  rum: datadogRum,
  service: DATADOG_SERVICE,
  version: DATADOG_VERSION
});

export const createDatadogRumInitializer = () => {
  let isInitialized = false;

  // Telemetry must never block application startup. Resolve configuration and
  // call the SDK inside one boundary so any failure stays local to this module.
  return (options?: DatadogRumInitOptions) => {
    try {
      const { appVersion, applicationId, clientToken, deploymentId, enabled, environment, remoteBaseUrl, rum, service, version } = options ?? getDefaultOptions();
      if (isInitialized || !enabled || !applicationId || !clientToken || !rum) {
        return;
      }

      rum.init({
        allowedTracingUrls: remoteBaseUrl ? [{ match: remoteBaseUrl, propagatorTypes: ['datadog'] }] : [],
        applicationId,
        clientToken,
        defaultPrivacyLevel: 'mask-user-input',
        env: environment,
        service,
        sessionSampleRate: 100,
        sessionReplaySampleRate: 100,
        site: 'datadoghq.com',
        trackLongTasks: true,
        trackResources: true,
        trackUserInteractions: true,
        version
      });
      isInitialized = true;

      if (appVersion) {
        rum.setGlobalContextProperty('app_version', appVersion);
      }
      if (deploymentId) {
        rum.setGlobalContextProperty('vercel_deployment_id', deploymentId);
      }
    } catch (error) {
      // eslint-disable-next-line no-console -- RUM initializes before the app logger, so console is the only sink here. Never rethrow: telemetry failures must not block startup.
      console.error('Failed to initialize Datadog RUM', error);
    }
  };
};

export const initDatadogRum = createDatadogRumInitializer();

initDatadogRum();
